import {
  Caption1,
  makeStyles,
  mergeClasses,
  shorthands,
  tokens,
} from "@fluentui/react-components";
import {
  Checkmark12Filled,
  TaskListSquareLtr16Regular,
} from "@fluentui/react-icons";
import type { DragEvent } from "react";
import type { ExecutionTask } from "@/engine/types";
import {
  ALL_LAYERS,
  LAYER_LABEL,
  type CalendarEvent,
  type CalendarLayer,
} from "@/features/calendar-view/calendar-layout";
import { MiniCalendar } from "@/features/calendar-view/MiniCalendar";
import { formatDate } from "@/lib/date-format";

const DND_TYPE = "application/x-wavex-execution-task";

export const LAYER_COLORS: Record<CalendarLayer, string> = {
  execution: "#f59e0b", // Amber / Orange
  activity: "#0284c7",  // Sky / Blue
  wbs: "#8b5cf6",       // Violet / Purple
  deadline: "#ef4444",  // Red
};

const useStyles = makeStyles({
  sidebar: {
    width: "256px",
    minWidth: "256px",
    maxWidth: "256px",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    backgroundColor: tokens.colorNeutralBackground1,
    borderRight: `1px solid ${tokens.colorNeutralStroke2}`,
    boxSizing: "border-box",
    overflowY: "auto",
    overflowX: "hidden",
  },
  section: {
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    borderTop: `1px solid ${tokens.colorNeutralStroke3}`,
    borderBottom: `1px solid ${tokens.colorNeutralStroke3}`,
  },
  sectionTitle: {
    fontSize: "11px",
    fontWeight: tokens.fontWeightRegular,
    color: tokens.colorNeutralForeground4,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    marginBottom: tokens.spacingVerticalS,
    display: "block",
  },
  layerItem: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
    padding: "4px 2px",
    cursor: "pointer",
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground1,
    borderRadius: tokens.borderRadiusSmall,
    userSelect: "none",
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  customCheckbox: {
    width: "16px",
    height: "16px",
    borderRadius: "4px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#ffffff",
    transition: "background-color 0.15s ease, opacity 0.15s ease",
  },
  layerLabel: {
    flex: 1,
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  listSection: {
    flex: 1,
    minHeight: "100px",
    display: "flex",
    flexDirection: "column",
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    borderBottom: `1px solid ${tokens.colorNeutralStroke3}`,
    minWidth: 0,
  },
  listSectionLast: {
    borderBottom: "none",
  },
  colorBar: {
    width: "3px",
    height: "14px",
    borderRadius: "1px",
    flexShrink: 0,
  },
  unscheduledList: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
    overflowY: "auto",
    paddingRight: "2px",
    marginTop: tokens.spacingVerticalXS,
  },
  unscheduledCard: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalXS,
    padding: "6px 8px",
    backgroundColor: tokens.colorNeutralBackground2,
    ...shorthands.border("1px", "solid", tokens.colorNeutralStroke2),
    borderRadius: tokens.borderRadiusMedium,
    fontSize: tokens.fontSizeBase200,
    cursor: "grab",
    transition: "all 0.15s ease",
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground2Hover,
      ...shorthands.borderColor(tokens.colorNeutralStroke1Hover),
    },
    ":active": {
      cursor: "grabbing",
    },
  },
  cardTitle: {
    flex: 1,
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    color: tokens.colorNeutralForeground1,
  },
  cardAssignee: {
    fontSize: "11px",
    color: tokens.colorNeutralForeground4,
    flexShrink: 0,
  },
  dragging: {
    opacity: 0.4,
  },
});

export interface CalendarSidebarProps {
  anchor: string;
  onAnchorChange: (iso: string) => void;
  // Layers
  layers: CalendarLayer[];
  onToggleLayer: (layer: CalendarLayer) => void;
  // Selected-day events
  dayEvents: CalendarEvent[];
  onEventClick: (event: CalendarEvent) => void;
  // Unscheduled tasks
  unscheduled: ExecutionTask[];
  onTaskClick: (taskId: string) => void;
  dragTaskId: string | null;
  canDrag: boolean;
  onDragStart: (taskId: string) => (e: DragEvent) => void;
  onDragEnd: () => void;
}

export function CalendarSidebar({
  anchor,
  onAnchorChange,
  layers,
  onToggleLayer,
  dayEvents,
  onEventClick,
  unscheduled,
  onTaskClick,
  dragTaskId,
  canDrag,
  onDragStart,
  onDragEnd,
}: CalendarSidebarProps) {
  const styles = useStyles();

  return (
    <aside className={styles.sidebar}>
      {/* 1. Mini Month Calendar */}
      <MiniCalendar value={anchor} onChange={onAnchorChange} />

      {/* 2. Layers (Color Checkboxes) */}
      <div className={styles.section}>
        <Caption1 className={styles.sectionTitle}>레이어</Caption1>
        {ALL_LAYERS.map((layer) => {
          const active = layers.includes(layer);
          const color = LAYER_COLORS[layer];

          return (
            <div
              key={layer}
              className={styles.layerItem}
              role="checkbox"
              aria-checked={active}
              tabIndex={0}
              onClick={() => onToggleLayer(layer)}
              onKeyDown={(e) => {
                if (e.key === " " || e.key === "Enter") {
                  e.preventDefault();
                  onToggleLayer(layer);
                }
              }}
            >
              <div
                className={styles.customCheckbox}
                style={{
                  backgroundColor: active ? color : "transparent",
                  border: `1.5px solid ${color}`,
                }}
              >
                {active && <Checkmark12Filled style={{ fontSize: 11 }} />}
              </div>
              <span className={styles.layerLabel}>{LAYER_LABEL[layer]}</span>
            </div>
          );
        })}
      </div>

      {/* 5. Selected-day task list */}
      <div className={styles.listSection}>
        <Caption1 className={styles.sectionTitle} style={{ marginBottom: 0 }}>
          {dayTitle(anchor)}
        </Caption1>

        {dayEvents.length === 0 ? (
          <Caption1 style={{ color: tokens.colorNeutralForeground4, marginTop: "8px" }}>
            이 날 일정이 없습니다.
          </Caption1>
        ) : (
          <div className={styles.unscheduledList}>
            {dayEvents.map((e) => {
              const isExec = e.kind === "execution";
              return (
                <div
                  key={e.id}
                  className={mergeClasses(
                    styles.unscheduledCard,
                    dragTaskId === e.refId && styles.dragging,
                  )}
                  draggable={isExec}
                  onDragStart={isExec ? onDragStart(e.refId) : undefined}
                  onDragEnd={onDragEnd}
                  onClick={() => onEventClick(e)}
                  title={e.title}
                >
                  <span
                    className={styles.colorBar}
                    style={{ backgroundColor: eventColor(e) }}
                  />
                  <span className={styles.cardTitle}>{e.title || "제목 없음"}</span>
                  {e.assignees?.[0] && (
                    <span className={styles.cardAssignee}>{e.assignees[0]}</span>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* 6. Unscheduled Tasks Tray Pool */}
      <div className={mergeClasses(styles.listSection, styles.listSectionLast)}>
        <Caption1 className={styles.sectionTitle} style={{ marginBottom: 0 }}>
          일정 미정
        </Caption1>

        {unscheduled.length === 0 ? (
          <Caption1 style={{ color: tokens.colorNeutralForeground4, marginTop: "8px" }}>
            미정 Task가 없습니다.
          </Caption1>
        ) : (
          <div className={styles.unscheduledList}>
            {unscheduled.map((t) => (
              <div
                key={t.id}
                className={mergeClasses(
                  styles.unscheduledCard,
                  dragTaskId === t.id && styles.dragging,
                )}
                draggable={canDrag}
                onDragStart={canDrag ? onDragStart(t.id) : undefined}
                onDragEnd={onDragEnd}
                onClick={() => onTaskClick(t.id)}
                title="날짜 셀로 끌어 놓아 마감일 지정"
              >
                <TaskListSquareLtr16Regular style={{ color: tokens.colorNeutralForeground3, flexShrink: 0 }} />
                <span className={styles.cardTitle}>{t.title || "제목 없음"}</span>
                {t.assignees[0] && (
                  <span className={styles.cardAssignee}>{t.assignees[0]}</span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </aside>
  );
}

function dayTitle(iso: string): string {
  return formatDate(iso, "monthDayWeekday");
}

function eventColor(event: CalendarEvent): string {
  if (event.kind === "execution") return event.overdue ? "#dc2626" : LAYER_COLORS.execution;
  if (event.kind === "milestone") return "#0d9488";
  if (event.kind === "activity") return event.critical ? "#dc2626" : LAYER_COLORS.activity;
  if (event.kind === "wbs") return LAYER_COLORS.wbs;
  return LAYER_COLORS.deadline;
}
